import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Card, CardContent, Typography, Box, Button, 
  Chip, Rating, Collapse, IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Calendar, Users, ChevronUp, ChevronDown, ChevronRight,
  BarChart2, BookmarkPlus, BookmarkMinus, Percent, Check
} from 'lucide-react';
import { trackLead } from '../../utils/analytics';

const CircleCard = ({ circle, onCompare, onSave, isComparing }) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [expanded, setExpanded] = useState(false);
  
  // Determine if the circle is full
  const isCircleFull = circle.available_slots <= 0;

  // Calculate the next tier price based on joining
  const getNextTierPrice = () => {
    if (!circle || !circle.pricing_tiers || circle.pricing_tiers.length === 0) {
      return circle.monthly_payment_per_line;
    }

    // Calculate total members after the user joins
    const totalMembersAfterJoin = (circle.family_size || 0) + (circle.total_joiners || 0) + 1;

    // Sort tiers by line_count to ensure proper ordering
    const sortedTiers = [...circle.pricing_tiers].sort((a, b) => a.line_count - b.line_count);

    // Find the appropriate tier based on the new total
    const nextTier = sortedTiers.find(tier => tier.line_count >= totalMembersAfterJoin);

    // Return appropriate price
    return nextTier ? nextTier.price_per_line :
      sortedTiers.length > 0 ? sortedTiers[sortedTiers.length - 1].price_per_line :
        circle.monthly_payment_per_line;
  };

  // Get the price to display
  const displayPrice = getNextTierPrice();

  // Calculate savings compared to standard rate
  const calculateSavings = () => {
    const standardRate = circle.carrier_name === 'Verizon' ? 70 :
      circle.carrier_name === 'AT&T' ? 65 :
        circle.carrier_name === 'T-Mobile' ? 60 : 55;
    const savings = standardRate - displayPrice;
    const percentage = Math.round((savings / standardRate) * 100);
    return { amount: savings > 0 ? savings.toFixed(0) : 0, percentage: percentage > 0 ? percentage : 0 };
  };

  const savings = calculateSavings();

  // Handle join click
  const handleJoinClick = async () => {
    if (!circle) {
      console.error('No circle data provided');
      return;
    }
    if (isCircleFull) {
      // Show a confirmation dialog for waitlist
      const confirmJoin = window.confirm(`Join the waitlist for ${circle.name}? We'll notify you when a spot opens up.`);

      if (!confirmJoin) {
        return; // User cancelled, don't proceed
      }

      // lead: only once the visitor has confirmed they want the waitlist
      trackLead(circle.id);

      try {
        // Get the Auth0 access token
        const token = await getAccessTokenSilently();
        
        // Make the waitlist API call with proper authentication
        const response = await fetch('/api/waitlist/join', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            circleId: circle.id
          }),
        });
        
        const data = await response.json();
        
        // Navigate to the circle detail page
        navigate(`/circle/${circle.id}`, {
          state: {
            circleId: circle.id,
            fallbackData: {
              circleName: circle.name,
              carrierName: circle.carrier_name,
              planName: circle.plan_name
            },
            joinWaitlist: true,
            waitlistPosition: data.position
          }
        });
      } catch (error) {
        console.error('Error joining waitlist:', error);
        
        // Still navigate to the circle page even if the API call fails
        navigate(`/circle/${circle.id}`, {
          state: {
            circleId: circle.id,
            fallbackData: {
              circleName: circle.name,
              carrierName: circle.carrier_name,
              planName: circle.plan_name
            },
            joinWaitlist: true
          }
        });
      }
    } else {
      // Regular join flow for available circles — no confirm gate
      trackLead(circle.id);
      navigate(`/circle/${circle.id}`, {
        state: {
          circleId: circle.id,
          fallbackData: {
            circleName: circle.name,
            carrierName: circle.carrier_name,
            planName: circle.plan_name
          },
          joinWaitlist: false
        }
      });
    }
  
    // Analytics tracking
    try {
      if (window.analytics) {
        window.analytics.track('Circle Selected', {
          circleId: circle.id,
          circleName: circle.name,
          carrier: circle.carrier_name,
          planName: circle.plan_name,
          price: displayPrice,
          isWaitlist: isCircleFull
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  // Get card background based on carrier
  const getCardBackground = () => {
    if (circle.carrier_name === 'Verizon') {
      return 'linear-gradient(135deg, #E31937, #990000)';
    } else if (circle.carrier_name === 'AT&T') {
      return 'linear-gradient(135deg, #0057B8, #003366)';
    } else if (circle.carrier_name === 'T-Mobile') {
      return 'linear-gradient(135deg, #E20074, #7F0F3F)';
    } else {
      return 'linear-gradient(135deg, #3ACCE1, #2E7D32)';
    }
  };

  // Get button colors based on carrier - UPDATED with even better color combinations
  const getButtonColors = () => {
    if (isCircleFull) {
      // For waitlist buttons - dark with light text
      if (circle.carrier_name === 'Verizon') {
        return {
          backgroundColor: 'rgba(77, 0, 0, 0.8)',
          color: '#ffffff',
          hoverBackgroundColor: 'rgba(100, 0, 0, 0.9)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 6px 12px rgba(150, 0, 0, 0.25)'
        };
      } else if (circle.carrier_name === 'AT&T') {
        return {
          backgroundColor: 'rgba(0, 45, 98, 0.8)',
          color: '#ffffff',
          hoverBackgroundColor: 'rgba(0, 60, 130, 0.9)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 6px 12px rgba(0, 45, 98, 0.25)'
        };
      } else if (circle.carrier_name === 'T-Mobile') {
        return {
          backgroundColor: 'rgba(110, 0, 60, 0.8)',
          color: '#ffffff',
          hoverBackgroundColor: 'rgba(130, 0, 70, 0.9)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 6px 12px rgba(110, 0, 60, 0.25)'
        };
      } else {
        return {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          hoverBackgroundColor: 'rgba(0, 0, 0, 0.6)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)'
        };
      }
    }

    // For regular join buttons - light with carrier-colored text
    if (circle.carrier_name === 'Verizon') {
      return {
        backgroundColor: '#ffecec',
        color: '#b50000', 
        hoverBackgroundColor: '#ffdfdf',
        borderColor: 'rgba(227, 25, 55, 0.3)',
        boxShadow: '0 6px 12px rgba(221, 0, 0, 0.15)'
      };
    } else if (circle.carrier_name === 'AT&T') {
      return {
        backgroundColor: '#e6f0ff',
        color: '#003e8a',
        hoverBackgroundColor: '#d9e7ff',
        borderColor: 'rgba(0, 87, 184, 0.3)',
        boxShadow: '0 6px 12px rgba(0, 87, 184, 0.15)'
      };
    } else if (circle.carrier_name === 'T-Mobile') {
      return {
        backgroundColor: '#ffe6f3',
        color: '#a10055',
        hoverBackgroundColor: '#ffd9ed',
        borderColor: 'rgba(226, 0, 116, 0.3)',
        boxShadow: '0 6px 12px rgba(226, 0, 116, 0.15)'
      };
    } else {
      return {
        backgroundColor: '#e6fff0',
        color: '#1e5924',
        hoverBackgroundColor: '#d9ffea',
        borderColor: 'rgba(46, 125, 50, 0.3)',
        boxShadow: '0 6px 12px rgba(46, 125, 50, 0.15)'
      };
    }
  };

  // Render particle elements for visual effect
  const renderParticles = () => {
    return [...Array(6)].map((_, i) => (
      <Box 
        key={i}
        component="div"
        sx={{
          position: 'absolute',
          width: i % 2 === 0 ? '6px' : '4px',
          height: i % 2 === 0 ? '6px' : '4px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          boxShadow: '0 0 6px rgba(255, 255, 255, 0.3)',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animation: `float ${Math.random() * 20 + 15}s linear infinite`,
          animationDelay: `-${Math.random() * 10}s`,
          opacity: Math.random() * 0.5 + 0.3,
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
    ));
  };

  // Get button styling
  const buttonColors = getButtonColors();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
      whileHover={{ 
        y: -8, 
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        transition: { duration: 0.3 }
      }}
      style={{ 
        height: '100%',
        width: '100%',
        borderRadius: '20px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <Card
        sx={{
          background: getCardBackground(),
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '20px',
          height: '100%',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          p: 0,
          transform: 'translateZ(0)', // Force GPU acceleration
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.2) 100%)',
            zIndex: 1,
            pointerEvents: 'none'
          }
        }}
      >
        {/* Glass overlay effect with light reflection */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, rgba(0,0,0,0.1) 100%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />
        
        {/* Particle effects for visual enhancement */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 1, pointerEvents: 'none' }}>
          {renderParticles()}
        </Box>

        {/* FULL indicator */}
        {isCircleFull && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              py: 0.5,
              px: 1.5,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              zIndex: 10,
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Box
              component={motion.div}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#ff3b5c',
                display: 'inline-block'
              }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
            FULL
          </Box>
        )}

        <CardContent sx={{ position: 'relative', zIndex: 3, p: 3, height: '100%' }}>
          {/* Circle Name and Price - Horizontal Layout */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            {/* IMPROVED: Enhanced title with better contrast */}
            {/* IMPROVED: Title with balanced brightness */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: '1.75rem',
                color: '#ffffff !important', // Force white with !important
                textShadow: '0 0 6px rgba(255,255,255,0.4)', // More subtle glow effect
                maxWidth: '60%',
                letterSpacing: '0.3px',
                position: 'relative',
                zIndex: 5,
                WebkitTextFillColor: 'white !important', // For webkit browsers
                '&&': { // Double selector increases specificity
                  color: '#ffffff'
                }
              }}
              style={{ color: 'white' }} // Additional inline style for extra override
            >
              {circle.name}
            </Typography>

            {/* Price display */}
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'white',
                    opacity: 0.9,
                  }}
                >
                  $
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2.75rem',
                    color: 'white',
                    lineHeight: 1,
                    mx: 0.5,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  {displayPrice}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: 'white',
                    opacity: 0.9,
                  }}
                >
                  /mo
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Verification, Rating and Savings - Horizontal Layout */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2.5 }}>
            <Chip
              icon={circle.owner_verified ? <Check size={14} /> : <AlertTriangle size={14} />}
              label={circle.owner_verified ? "Verified" : "Unverified"}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                color: 'white',
                height: '28px',
                fontSize: '0.8rem',
                fontWeight: 600,
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '& .MuiChip-label': { px: 1 },
                '& .MuiChip-icon': { color: 'white' }
              }}
            />

            <Box sx={{ display: 'flex', ml: 0.5 }}>
              <Rating
                name="read-only"
                value={circle.rating || 4}
                readOnly
                size="small"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#FFD700',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              />
            </Box>

            {savings.amount > 0 && (
              <Chip
                icon={<Percent size={14} />}
                label={`Save $${savings.amount} (${savings.percentage}%)`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(8px)',
                  color: 'white',
                  height: '28px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  borderRadius: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  ml: 'auto',
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            )}
          </Box>

          {/* Carrier & Plan */}
          <Box 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(8px)',
              p: 1.5, 
              borderRadius: '12px',
              mb: 2.5,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  backgroundColor: 'white',
                  mr: 1.5,
                  boxShadow: '0 0 8px rgba(255,255,255,0.5)'
                }} 
              />
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                {circle.carrier_name} - {circle.plan_name}
              </Typography>
            </Box>
          </Box>

          {/* Plan highlights section */}
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
              borderRadius: '12px',
              mb: 2.5,
              p: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Plan Highlights
              </Typography>
              <Box 
                sx={{ 
                  ml: 1.5, 
                  height: '1px', 
                  flexGrow: 1, 
                  background: 'linear-gradient(to right, rgba(255,255,255,0.5), transparent)' 
                }} 
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {circle.features?.filter(f => f.is_primary).map((feature, index) => (
                <Chip
                  key={index}
                  label={feature.short_description}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(4px)',
                    color: 'white',
                    height: '28px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderRadius: '14px',
                    '& .MuiChip-label': { px: 1.5 },
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Availability Status Row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
              }}
            >
              <Users size={16} color="white" />
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: isCircleFull ? 600 : 500,
                  fontSize: '0.85rem',
                }}
              >
                {isCircleFull ?
                  "Currently full" :
                  `${circle.available_slots} spot${circle.available_slots !== 1 ? 's' : ''} left`
                }
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
              }}
            >
              <Calendar size={14} color="white" />
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontSize: '0.8rem',
                }}
              >
                Since {new Date(circle.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric'
                })}
              </Typography>
            </Box>
          </Box>

          {/* IMPROVED: Join Button with overridden theme styles */}
          <Button
            variant="contained"
            color="inherit" /* Added to prevent theme primary color override */
            endIcon={<ChevronRight size={16} />}
            onClick={handleJoinClick}
            fullWidth
            component={motion.button}
            whileHover={{ y: -3, boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}
            whileTap={{ y: 0, boxShadow: '0 5px 10px rgba(0,0,0,0.2)' }}
            sx={{
              backgroundColor: `${buttonColors.backgroundColor} !important`, /* Force our colors */
              color: `${buttonColors.color} !important`,
              fontWeight: 700,
              textTransform: 'none',
              height: '44px',
              borderRadius: '22px',
              fontSize: '0.95rem',
              mb: 1.5,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                backgroundColor: `${buttonColors.hoverBackgroundColor} !important`,
              },
              border: isCircleFull ? `1px solid ${buttonColors.borderColor}` : 'none',
              boxShadow: isCircleFull ? 'none' : buttonColors.boxShadow || '0 5px 15px rgba(0, 0, 0, 0.15)',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                animation: 'shine 2s infinite',
                '@keyframes shine': {
                  '0%': { left: '-100%' },
                  '50%': { left: '200%' },
                  '100%': { left: '200%' }
                }
              }
            }}
          >
            {isCircleFull ? 'Join Waitlist' : 'Join Circle'}
          </Button>

          {/* More Info Button */}
          <Button
            fullWidth
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 500,
              height: '38px',
              borderRadius: '19px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
            }}
          >
            {expanded ? 'Less Info' : 'More Info'}
          </Button>

          {/* IMPROVED: Expanded Section with better text alignment */}
          <Collapse in={expanded}>
            <Box 
              mt={2} 
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(8px)',
                p: 2,
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              {/* Plan Features Section - IMPROVED TEXT ALIGNMENT */}
              <Box mb={2.5}>
                <Typography
                  component="div"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'white',
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    '&::after': {
                      content: '""',
                      flex: 1,
                      height: '1px',
                      background: 'linear-gradient(to right, rgba(255,255,255,0.3), transparent)',
                      ml: 1
                    }
                  }}
                >
                  Plan Features
                </Typography>
                
                {/* IMPROVED: Better aligned list with proper spacing and alignment */}
                <ul style={{
                  listStyleType: 'none',
                  padding: 0,
                  margin: '8px 0 0 0'
                }}>
                  {circle.features?.map((f, index) => (
                    <li 
                      key={index} 
                      style={{
                        position: 'relative',
                        paddingLeft: '20px',
                        marginBottom: '10px',
                        color: 'white',
                        lineHeight: 1.5,
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'flex-start',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: '6px',
                        top: '4px',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '12px'
                      }}>•</span>
                      <span style={{ display: 'block', textAlign: 'left' }}>
                        {f.full_description || f.short_description}
                      </span>
                    </li>
                  ))}
                </ul>
              </Box>

              {/* Circle Rules & Payment in same row */}
              <Box 
                sx={{
                  display: 'flex', 
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 2
                }}
              >
                {/* Circle Rules Section */}
                <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: 'white',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      '&::after': {
                        content: '""',
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(to right, rgba(255,255,255,0.3), transparent)',
                        ml: 1
                      }
                    }}
                  >
                    Circle Rules
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontSize: '0.8rem',
                      lineHeight: 1.5,
                      textAlign: 'left' // Ensure left alignment
                    }}
                  >
                    {circle.notes || "Standard circle rules apply"}
                  </Typography>
                </Box>

                {/* Payment Schedule Section */}
                <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: 'white',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      '&::after': {
                        content: '""',
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(to right, rgba(255,255,255,0.3), transparent)',
                        ml: 1
                      }
                    }}
                  >
                    Payment Schedule
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontSize: '0.8rem',
                      lineHeight: 1.5,
                      textAlign: 'left' // Ensure left alignment
                    }}
                  >
                    Due on the {new Date(circle.monthly_payment_date || new Date()).getDate()}th of each month
                  </Typography>
                </Box>
              </Box>

              {/* Waitlist info box - with improved alignment */}
              {isCircleFull && (
                <Box
                  sx={{
                    background: 'rgba(0,0,0,0.2)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    p: 1.5,
                    mb: 2,
                    borderLeft: '3px solid rgba(255,255,255,0.2)',
                    textAlign: 'left' // Ensure left alignment
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontSize: '0.8rem',
                      lineHeight: 1.5
                    }}
                  >
                    <span style={{ fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                      Waitlist Information
                    </span>
                    This circle is at maximum capacity. Join the waitlist to be notified when a spot opens up. Average wait time: ~2-3 weeks.
                  </Typography>
                </Box>
              )}

              {/* Compare & Save buttons */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mt: 1.5
                }}
              >
                <Button
                  startIcon={<BarChart2 size={14} />}
                  onClick={(e) => { e.stopPropagation(); onCompare(circle); }}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: isComparing ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    backdropFilter: 'blur(8px)',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    padding: '3px 10px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    }
                  }}
                >
                  {isComparing ? 'Remove' : 'Compare'}
                </Button>
                
                <IconButton
                  onClick={(e) => { e.stopPropagation(); onSave(circle); }}
                  size="small"
                  sx={{ 
                    color: 'white',
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    }
                  }}
                >
                  {circle.isSaved ? <BookmarkMinus size={16} /> : <BookmarkPlus size={16} />}
                </IconButton>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CircleCard;